import React, { useCallback, useEffect, useState } from 'react';
import './PushToBI.css';
import { apiService } from '../../../services/api';
import PushToBIWizard from './PushToBIWizard';
import RunHistoryDrawer from './RunHistoryDrawer';
import Pagination from '../shared/Pagination';
import Chip from '../shared/Chip';
import Timestamp from '../shared/Timestamp';
import { useStatusStream } from '../../../services/useStatusStream';

// Top-level Push-to-BI page. Lists configured push automations + run history
// drawer, and launches the wizard. Routed at /push-to-bi from App.js.
const PushToBI = ({ onShowNotification }) => {
  const [automations, setAutomations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [drawerAutomation, setDrawerAutomation] = useState(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const pageSize = 20;

  // Live status surface (§6.4). Stub today; real stream lands with TRIB-B-STREAM-INTEGRATE.
  const stream = useStatusStream('push-automations');

  const load = useCallback(async (pageNum = 1) => {
    try {
      setLoading(true);
      const resp = await apiService.listPushAutomations({ page: pageNum, page_size: pageSize });
      const items = resp?.items ?? (Array.isArray(resp) ? resp : []);
      setAutomations(items);
      setTotal(resp?.total ?? items.length);
      setPage(pageNum);
    } catch (err) {
      console.error('[PushToBI] load failed', err);
      setAutomations([]);
      setTotal(0);
      if (onShowNotification) onShowNotification(err.message || 'Failed to load push automations', 'error');
    } finally {
      setLoading(false);
    }
  }, [onShowNotification]);

  useEffect(() => { load(1); }, [load]);

  const handleWizardClose = useCallback((created) => {
    setWizardOpen(false);
    if (created && onShowNotification) onShowNotification('Push automation created', 'success');
    load(page);
  }, [load, page, onShowNotification]);

  const handleRunNow = async (automation) => {
    try {
      await apiService.runPushAutomationNow(automation.id);
      if (onShowNotification) onShowNotification(`Run queued for ${automation.name}`, 'success');
      load(page);
    } catch (err) {
      if (onShowNotification) onShowNotification(err.message || 'Failed to start run', 'error');
    }
  };

  const handlePauseResume = async (automation) => {
    try {
      if (automation.enabled) {
        await apiService.pausePushAutomation(automation.id);
      } else {
        await apiService.resumePushAutomation(automation.id);
      }
      load(page);
    } catch (err) {
      if (onShowNotification) onShowNotification(err.message || 'Failed to update automation', 'error');
    }
  };

  if (wizardOpen) {
    return <PushToBIWizard isOpen={wizardOpen} onClose={handleWizardClose} onShowNotification={onShowNotification} />;
  }

  return (
    <div className="push-to-bi-page">
      <div className="push-to-bi-header">
        <button className="push-to-bi-new-button" onClick={() => setWizardOpen(true)}>
          New push automation
        </button>
      </div>
      {stream.error && (
        <div className="push-to-bi-stream-banner" role="status">Live status unavailable: {String(stream.error)}</div>
      )}
      <div className="push-to-bi-content">
        {loading ? (
          <div className="push-to-bi-loading"><p>Loading push automations...</p></div>
        ) : automations.length === 0 ? (
          <div className="push-to-bi-empty">
            <p>No push automations yet. Click "New push automation" to create one.</p>
          </div>
        ) : (
          <>
            <ul className="push-to-bi-list" data-testid="push-automation-list">
              {automations.map((a) => (
                <li key={a.id} className="push-automation-card">
                  <div className="push-automation-card-header">
                    <h3>{a.name}</h3>
                    <Chip
                      variant={a.enabled ? 'success' : 'alert'}
                      className="push-automation-status"
                    >
                      {a.enabled ? 'Active' : 'Paused'}
                    </Chip>
                  </div>
                  {a.description && <p className="push-automation-card-desc">{a.description}</p>}
                  <div className="push-automation-card-meta">
                    <span><strong>Target:</strong> {a.target_table || a.target_dataset || '—'}</span>
                    <span><strong>Connection:</strong> {a.connection_kind || '—'}</span>
                    <span><strong>Mode:</strong> {a.tool_hint || '—'}</span>
                    <span><strong>Last run:</strong> {a.last_run_at ? <Timestamp value={a.last_run_at} /> : 'Not run yet'}</span>
                  </div>
                  <div className="push-automation-card-actions">
                    <button className="push-action-button" onClick={() => handleRunNow(a)}>Run now</button>
                    <button className="push-action-button" onClick={() => handlePauseResume(a)}>
                      {a.enabled ? 'Pause' : 'Resume'}
                    </button>
                    <button className="push-action-button" onClick={() => setDrawerAutomation(a)}>Run history</button>
                  </div>
                </li>
              ))}
            </ul>
            <Pagination currentPage={page} totalCount={total} pageSize={pageSize} onPageChange={load} />
          </>
        )}
      </div>
      {drawerAutomation && (
        <RunHistoryDrawer
          automation={drawerAutomation}
          onClose={() => setDrawerAutomation(null)}
          onShowNotification={onShowNotification}
        />
      )}
    </div>
  );
};

export default PushToBI;
